import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button, Card, Col, DatePicker, Input, Row, Select, Space, Table, Tag, Tooltip, Typography } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';

import Header from '../../components/Header';
import {
	KeyenceValidationReport,
	KeyenceValidationReportQuery,
} from '../../interfaces/Quality.Interface';
import {
	useGetKeyenceSystemsQuery,
	useGetKeyenceValidationReportsQuery,
	useGetKeyenceValidationStatsQuery,
} from '../../redux/api/quality';
import ReportDrawer from './ReportDrawer';
import StatsPanel from './StatsPanel';
import {
	ENFORCEMENT_COLOR,
	ENFORCEMENT_OPTIONS,
	OUTCOME_COLOR,
	OUTCOME_OPTIONS,
	PAGE_SIZE_OPTIONS,
	SEVERITY_OPTIONS,
	SOURCE_OPTIONS,
	findingCodeLabel,
} from './constants';

const { Text } = Typography;
const { RangePicker } = DatePicker;

/** Every filter lives in the URL so a report list view can be linked and shared. */
const FILTER_KEYS = [
	'search',
	'outcome',
	'enforcement',
	'source',
	'keyence_device',
	'code',
	'severity',
	'created_after',
	'created_before',
] as const;

type FilterKey = (typeof FILTER_KEYS)[number];

const KeyenceValidationReport = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	const [selected, setSelected] = useState<KeyenceValidationReport | null>(null);

	const param = useCallback(
		(key: string) => searchParams.get(key) || undefined,
		[searchParams]
	);

	const [searchInput, setSearchInput] = useState(param('search') ?? '');
	useEffect(() => {
		setSearchInput(param('search') ?? '');
	}, [param]);

	const page = Number(param('page') ?? 1);
	const pageSize = Number(param('page_size') ?? 25);

	const setParams = useCallback(
		(updates: Record<string, string | undefined>, resetPage = true) => {
			setSearchParams(
				(prev) => {
					const next = new URLSearchParams(prev.toString());
					Object.entries(updates).forEach(([key, value]) => {
						if (value === undefined || value === '') next.delete(key);
						else next.set(key, value);
					});
					if (resetPage && !('page' in updates)) next.delete('page');
					return next;
				},
				{ replace: true }
			);
		},
		[setSearchParams]
	);

	const filters = useMemo(() => {
		const built: KeyenceValidationReportQuery = {};
		FILTER_KEYS.forEach((key: FilterKey) => {
			const value = param(key);
			if (value) built[key] = value;
		});
		return built;
	}, [param]);

	const activeFilterCount = Object.keys(filters).length;

	const { data, isFetching } = useGetKeyenceValidationReportsQuery({
		...filters,
		page,
		page_size: pageSize,
		ordering: param('ordering'),
	});
	const { data: stats, isFetching: statsFetching } =
		useGetKeyenceValidationStatsQuery(filters);
	const { data: devices } = useGetKeyenceSystemsQuery();

	const codeOptions = useMemo(() => {
		const codes = new Set(stats?.top_finding_codes.map((c) => c.code) ?? []);
		if (filters.code) codes.add(filters.code);
		return [...codes].map((code) => ({
			value: code,
			label: findingCodeLabel(code),
		}));
	}, [stats, filters.code]);

	const dateRange: [dayjs.Dayjs, dayjs.Dayjs] | null =
		filters.created_after && filters.created_before
			? [dayjs(filters.created_after), dayjs(filters.created_before)]
			: null;

	const columns: ColumnsType<KeyenceValidationReport> = [
		{
			title: 'Checked',
			dataIndex: 'created_time',
			width: 160,
			sorter: true,
			defaultSortOrder: 'descend',
			render: (time: string) => dayjs(time).format('MMM D, YYYY h:mm A'),
		},
		{
			title: 'Part',
			dataIndex: 'part_name',
			width: 200,
			render: (part: string | null, row) => (
				<div>
					<div>{part ?? '—'}</div>
					{row.order_name && <Text type="secondary">{row.order_name}</Text>}
				</div>
			),
		},
		{
			title: 'Program file',
			dataIndex: 'file_name',
			width: 220,
			ellipsis: true,
		},
		{ title: 'View', dataIndex: 'view', width: 90 },
		{
			title: 'Device',
			dataIndex: 'keyence_device_name',
			width: 130,
			render: (name: string | null) => name ?? '—',
		},
		{
			title: 'Result',
			dataIndex: 'outcome',
			width: 190,
			render: (_, row) => (
				<Space wrap size={4}>
					<Tag color={OUTCOME_COLOR[row.outcome]}>{row.outcome}</Tag>
					{row.enforcement !== 'Accepted' && (
						<Tag color={ENFORCEMENT_COLOR[row.enforcement]}>
							{row.enforcement}
						</Tag>
					)}
				</Space>
			),
		},
		{
			title: 'Errors',
			dataIndex: 'error_count',
			width: 90,
			sorter: true,
			align: 'right',
			render: (count: number) =>
				count > 0 ? <Text type="danger">{count}</Text> : count,
		},
		{
			title: 'Warnings',
			dataIndex: 'warning_count',
			width: 100,
			sorter: true,
			align: 'right',
			render: (count: number) =>
				count > 0 ? <Text type="warning">{count}</Text> : count,
		},
		{
			title: 'Coverage',
			width: 110,
			align: 'right',
			render: (_, row) => (
				<Tooltip
					title={`${row.matched_count} of ${row.plan_balloon_count} plan balloons matched by ${row.program_measurement_count} program measurements`}
				>
					<span>
						{row.matched_count}/{row.plan_balloon_count}
					</span>
				</Tooltip>
			),
		},
		{
			title: 'Issues',
			dataIndex: 'finding_codes',
			width: 280,
			render: (codes: string[] | undefined) =>
				codes && codes.length > 0 ? (
					<Space wrap size={4}>
						{codes.map((code) => (
							<Tag key={code}>{findingCodeLabel(code)}</Tag>
						))}
					</Space>
				) : (
					<Text type="secondary">—</Text>
				),
		},
		{
			title: 'Uploaded by',
			dataIndex: 'uploaded_by_name',
			width: 160,
			render: (name: string | null) => name ?? '—',
		},
	];

	return (
		<>
			<Header
				title="Keyence Validation Reports"
				breadcrumbItems={[
					{ title: <Link to="/">Home</Link> },
					{ title: 'Quality' },
					{ title: 'Keyence Validation Reports' },
				]}
			/>

			<Row gutter={[8, 12]}>
				<Col span={24}>
					<StatsPanel stats={stats} loading={statsFetching} />
				</Col>
				<Col span={24}>
					<Card>
						<Row gutter={[8, 12]}>
							<Col span={24}>
								<Space wrap size="small">
									<Input
										allowClear
										prefix={<SearchOutlined />}
										placeholder="Search file, part or order"
										style={{ width: 260 }}
										value={searchInput}
										onChange={(e) => setSearchInput(e.target.value)}
										onPressEnter={() => setParams({ search: searchInput })}
										onBlur={() => setParams({ search: searchInput })}
									/>
									<Select
										allowClear
										placeholder="Outcome"
										style={{ width: 150 }}
										value={filters.outcome}
										options={OUTCOME_OPTIONS}
										onChange={(outcome) => setParams({ outcome })}
									/>
									<Select
										allowClear
										placeholder="Enforcement"
										style={{ width: 170 }}
										value={filters.enforcement}
										options={ENFORCEMENT_OPTIONS}
										onChange={(enforcement) => setParams({ enforcement })}
									/>
									<Select
										allowClear
										placeholder="Source"
										style={{ width: 150 }}
										value={filters.source}
										options={SOURCE_OPTIONS}
										onChange={(source) => setParams({ source })}
									/>
									<Select
										allowClear
										placeholder="Device"
										style={{ width: 170 }}
										value={filters.keyence_device}
										options={(devices ?? []).map((device) => ({
											value: device.id,
											label: device.name,
										}))}
										onChange={(keyence_device) =>
											setParams({ keyence_device })
										}
									/>
									<Select
										allowClear
										placeholder="Issue"
										style={{ width: 220 }}
										value={filters.code}
										options={codeOptions}
										onChange={(code) => setParams({ code })}
									/>
									<Select
										allowClear
										placeholder="Severity"
										style={{ width: 130 }}
										value={filters.severity}
										options={SEVERITY_OPTIONS}
										onChange={(severity) => setParams({ severity })}
									/>
									<RangePicker
										value={dateRange}
										onChange={(dates) =>
											setParams({
												created_after: dates?.[0]
													? dates[0].startOf('day').toISOString()
													: undefined,
												created_before: dates?.[1]
													? dates[1].endOf('day').toISOString()
													: undefined,
											})
										}
									/>
									{activeFilterCount > 0 && (
										<Button
											type="link"
											onClick={() => {
												setSearchInput('');
												setParams(
													Object.fromEntries(
														FILTER_KEYS.map((key) => [key, undefined])
													)
												);
											}}
										>
											Clear filters
										</Button>
									)}
								</Space>
							</Col>
							<Col span={24}>
								<Table
									size="small"
									rowKey="id"
									loading={isFetching}
									dataSource={data?.results ?? []}
									columns={columns}
									scroll={{ x: 1700 }}
									onRow={(row) => ({
										onClick: () => setSelected(row),
										style: { cursor: 'pointer' },
									})}
									onChange={(pagination, _tableFilters, sorter, { action }) => {
										const single = Array.isArray(sorter) ? sorter[0] : sorter;
										const field = single?.field as string | undefined;
										setParams(
											{
												page:
													action === 'paginate'
														? String(pagination.current ?? 1)
														: undefined,
												page_size: String(pagination.pageSize ?? pageSize),
												ordering:
													field && single?.order
														? single.order === 'ascend'
															? field
															: `-${field}`
														: undefined,
											},
											false
										);
									}}
									pagination={{
										current: page,
										pageSize,
										total: data?.count ?? 0,
										showSizeChanger: true,
										pageSizeOptions: PAGE_SIZE_OPTIONS,
										showTotal: (total) => `${total} reports`,
									}}
								/>
							</Col>
						</Row>
					</Card>
				</Col>
			</Row>

			<ReportDrawer report={selected} onClose={() => setSelected(null)} />
		</>
	);
};

export default KeyenceValidationReport;
